import React, { useEffect, useState } from "react";
import { Skeleton } from "antd";
import BrandExclusiveDealsApi from "../../../../api-services/brand-exclusive-deals-api.service";
import type { BrandExclusiveDeal } from "../../../../api-services/brand-exclusive-deals-api.service";
import type { Deal } from "../../../../interfaces/deal.interface";
import type { TravelMode } from "../Homepage";
import DealCard from "./DealCard";
import SectionHeader from "./SectionHeader";
import ScrollCarousel from "./ScrollCarousel";

const toDeal = (deal: BrandExclusiveDeal): Deal => ({
  id: Number(deal.id),
  headline: deal.highlight,
  title: deal.title,
  subtitle: deal.subtitle,
  cta: "View Product",
  redirectUrl: deal.redirectUrl,
});

// "Exclusive Deals" - mode-aware, API-backed ticket cards. The full paginated
// listing remains on the v2-only route added by issue 641.
export default function ExclusiveDeals({ mode }: { mode: TravelMode }) {
  const [deals, setDeals] = useState<BrandExclusiveDeal[]>([]);
  const [loading, setLoading] = useState(true);
  const [fading, setFading] = useState(false);
  const categoryType = mode === "cruise" ? "cruise" : "series";

  useEffect(() => {
    let active = true;
    const hasCurrentDeals = deals.length > 0;
    const fadeDelay = hasCurrentDeals ? 180 : 0;

    if (hasCurrentDeals) {
      setFading(true);
    } else {
      setLoading(true);
    }

    const timer = window.setTimeout(() => {
      BrandExclusiveDealsApi.getDeals(categoryType)
        .then((nextDeals) => {
          if (active) setDeals(nextDeals);
        })
        .catch(() => {
          if (active) setDeals([]);
        })
        .finally(() => {
          if (!active) return;

          setLoading(false);
          window.requestAnimationFrame(() => setFading(false));
        });
    }, fadeDelay);

    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [categoryType]);

  if (!loading && deals.length === 0) return null;

  return (
    <section className="v2-section" id="exclusive-deals-v2">
      <SectionHeader title="Exclusive Deals" subtitle="Limited-time offers you shouldn't miss." viewAllHref="/web/exclusive-deals" />
      <ScrollCarousel trackClassName={`v2-deals__track ${fading ? "is-fading" : ""}`} step={360}>
        {loading && deals.length === 0 && [1, 2, 3].map((placeholder) => (
          <div key={placeholder} className="v2-deal-card">
            <div className="v2-deal-card__headline">
              <Skeleton.Input active size="small" />
            </div>
            <span className="v2-deal-card__perf" aria-hidden="true" />
            <div className="v2-deal-card__body">
              <Skeleton active paragraph={{ rows: 1 }} title={{ width: "80%" }} />
            </div>
          </div>
        ))}

        {deals.map((deal) => (
          <DealCard key={deal.id} deal={toDeal(deal)} />
        ))}
      </ScrollCarousel>
    </section>
  );
}
